import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PauseCircle, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const DISMISS_KEY = "institution_paused_notice_dismissed";

/**
 * Members whose institution contract is PAUSED can still sign in, but their
 * institution access (library + AI tutor allowances) is off. This tells them
 * why, instead of the library silently locking. Rendered once from each
 * header; portaled to <body> because the headers' backdrop-blur would trap a
 * fixed-position child. Dismissible for the browser session.
 */
export function InstitutionPausedNotice() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  const { data: notice } = useQuery({
    queryKey: ["institution-access-notice", user?.id],
    enabled: !!user?.id && !dismissed,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("institution_access_notice");
      if (error) return null; // never block the page on this
      const row = Array.isArray(data) ? data[0] : data;
      return row?.state === "paused" ? (row as { state: string; institution_name: string }) : null;
    },
  });

  if (!notice || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* private mode */ }
  };

  return createPortal(
    <div role="status" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] w-[calc(100%-2rem)] max-w-xl">
      <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 shadow-lg">
        <PauseCircle className="h-5 w-5 shrink-0 mt-0.5 text-amber-600" />
        <div className="flex-1 text-sm leading-snug">
          <p className="font-semibold">
            {es
              ? `El acceso de ${notice.institution_name} está en pausa`
              : `${notice.institution_name} access is paused`}
          </p>
          <p className="text-amber-900/80">
            {es
              ? "Tu institución pausó temporalmente el acceso a la biblioteca y al tutor IA. Contacta a tu institución para más detalles."
              : "Your institution has temporarily paused access to the library and the AI tutor. Please contact your institution for details."}
          </p>
        </div>
        <button type="button" onClick={dismiss} className="shrink-0 rounded p-1 hover:bg-amber-100" aria-label={es ? "Cerrar" : "Dismiss"}>
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>,
    document.body,
  );
}
